import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Globe,
  Server,
  Network,
  Truck,
  Cable,
  Layers,
  Check,
  ChevronDown,
  ArrowRight,
  FileText,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import { SOLUTIONS, EXTENDED_SOLUTIONS } from '../config/siteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';

const ICONS = { Globe, Server, Network, Truck, Cable, Layers };

/**
 * SolutionsIndex - single-page accordion (May 2026 redesign).
 *
 * All 6 iConnect solutions live here as expandable sections instead of 6
 * separate pages. Each item shows:
 *   - Icon + title + subtitle
 *   - Optional badge ("Most Popular", "Auto Trusted")
 *   - Description
 *   - Full feature list
 *   - "Schedule Demo" CTA
 *
 * Deep-link support: `/solutions/iexchangeweb` auto-opens that section and
 * scrolls to it (clean URL, no `#`). Legacy `/solutions#iexchangeweb` hash
 * fragments are still honored for backwards compat with old bookmarks.
 */
const ALL_SOLUTIONS = [
  ...SOLUTIONS,
  EXTENDED_SOLUTIONS['hybrid-van'],
  EXTENDED_SOLUTIONS.oftp2,
];

const SolutionsIndex = () => {
  useDocumentTitle({
    title: 'Solutions',
    description:
      'iConnect EDI solutions: iExchangeWeb (Web EDI), Managed EDI Services, VAN, Supplier EDI, Hybrid VAN, and OFTP2. Pick the right path for your team.',
  });

  const location = useLocation();
  const { slug: routeSlug } = useParams();

  // Compute the initial open set synchronously so the motion.div for a
  // route-slug accordion mounts already in the "open" state. Otherwise the
  // AnimatePresence `initial={false}` quirk leaves the content stuck at
  // height: 0 when the item opens via a post-mount setState.
  const [openIds, setOpenIds] = useState(() => {
    const initial = new Set([ALL_SOLUTIONS[0].id]);
    const slug = routeSlug || (location.hash || '').replace('#', '');
    if (slug && ALL_SOLUTIONS.some((s) => s.id === slug)) initial.add(slug);
    return initial;
  });

  // Handle client-side navigation between slugs after mount (e.g., user clicks
  // a Solutions dropdown item from another page already loaded).
  useEffect(() => {
    const id = routeSlug || location.hash.replace('#', '');
    if (!id) return;
    const target = ALL_SOLUTIONS.find((s) => s.id === id);
    if (!target) return;
    setOpenIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    // setTimeout > requestAnimationFrame here: StrictMode's effect-rerun pattern
    // and framer-motion's animation pipeline both interfere with rAF callbacks
    // on initial mount. A small delay lets layout settle deterministically.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 60);
    return () => clearTimeout(t);
  }, [routeSlug, location.hash]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <PageHero
        eyebrow="Solutions"
        title="One platform,"
        titleAccent="every EDI need."
        breadcrumbs={[{ label: 'Solutions' }]}
        subhead="From web-based EDI to fully managed integration - six paths, all running on the same VAN. Click to expand each."
      />

      <section className="py-16 lg:py-20 bg-bg-canvas">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 space-y-3">
          {ALL_SOLUTIONS.map((solution) => {
            const Icon = ICONS[solution.icon] || Layers;
            const isOpen = openIds.has(solution.id);
            return (
              <div
                key={solution.id}
                id={solution.id}
                className="card-elevated overflow-hidden scroll-mt-24"
              >
                <button
                  onClick={() => toggle(solution.id)}
                  className="w-full px-6 py-5 lg:px-8 lg:py-6 flex items-center justify-between gap-4 text-left group"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-gradient-sky shadow-glow-sky flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-white" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h3 className="font-display font-extrabold text-lg lg:text-xl text-text-primary tracking-tight">
                          {solution.title}
                        </h3>
                        {solution.badge && (
                          <span className="badge-sky">{solution.badge}</span>
                        )}
                      </div>
                      <div className="text-sm font-mono uppercase tracking-wider text-accent-deep">
                        {solution.subtitle}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`w-9 h-9 rounded-full bg-bg-soft border border-border-subtle flex items-center justify-center transition-transform group-hover:border-accent-primary/40 flex-shrink-0 ${
                      isOpen ? 'rotate-180 bg-accent-soft border-accent-primary/40' : ''
                    }`}
                  >
                    <ChevronDown
                      size={18}
                      className={isOpen ? 'text-accent-primary' : 'text-text-muted'}
                    />
                  </div>
                </button>

                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                      <div className="px-6 pb-6 lg:px-8 lg:pb-8 border-t border-border-subtle pt-5">
                        <p className="text-text-secondary leading-relaxed mb-6">
                          {solution.description}
                        </p>

                        <ul className="grid sm:grid-cols-2 gap-2.5 mb-6">
                          {solution.features.map((feature, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2.5 text-sm text-text-primary"
                            >
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-soft flex items-center justify-center mt-0.5">
                                <Check
                                  size={12}
                                  className="text-accent-primary"
                                  strokeWidth={3}
                                />
                              </span>
                              {feature}
                            </li>
                          ))}
                        </ul>

                        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                          <Link
                            to="/contact"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-primary hover:text-accent-bright transition-colors"
                          >
                            Schedule a demo for {solution.title}
                            <ArrowRight size={14} />
                          </Link>
                          {solution.pdf && (
                            <a
                              href={solution.pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-accent-primary transition-colors"
                            >
                              <FileText size={14} />
                              Download datasheet (PDF)
                            </a>
                          )}
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default SolutionsIndex;
